import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { useLayoutStyles } from './layout.styles';
import { LoadingIndicator } from '../../ui/components/pure/loading-indicator/loading-indicator.component';
import { NavigationBarContainer } from '../navigation/navigation-bar.container';
import { KioskConnectivityStatusBarContainer } from '../kiosk-connectivity-status-bar/kiosk-connectivity-status-bar.container';
import { AppBar } from '../../ui/components/pure/app-bar/app.bar.component';
import { useRouteMatch } from 'react-router-dom';
import { PathParams, Paths } from '../router/app.router';
import { PermissionBoundary } from '../../core/permissions/permission-boundary.component';
import { useEnvironment } from '../../core/env/environment.context';
import { Permissions } from '../../core/permissions/permissions.typings';
import { ProjectSummaryContextProvider, ProjectSummaryDrawerContainer } from './project-summary.context';
import { SearchContextProvider, SearchDrawerContainer } from './search.context';
import { useOptionalCurrentProject } from '../../core/context/project.context';
import { isCarrierProjectType } from '../navigation/courier-project-type';

export const Layout = ({
  children,
  params,
  loading
}: {
  children?: JSX.Element | JSX.Element[];
  params?: { projectID?: string; shortcode?: string };
  loading?: boolean;
}) => {
  const environment = useEnvironment();
  const classes = useLayoutStyles({ environment });

  // Hide the kiosk connectivity status bar on the order details page
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const match = useRouteMatch<PathParams<Paths.order> | PathParams<Paths.delivery>>([Paths.order, Paths.delivery]);

  // Some project types do not need the kiosk connectivity status bar.
  const currentProject = useOptionalCurrentProject();
  const hideKioskConnectivity =
    (!!match && isSm) ||
    !currentProject ||
    currentProject.isLoading ||
    isCarrierProjectType(currentProject.project?.projectType);

  return (
    <Box data-layout="iamlayout" className={classes.root}>
      {loading === true ? (
        <LoadingIndicator />
      ) : (
        <ProjectSummaryContextProvider>
          <SearchContextProvider>
            <AppBar>
              <NavigationBarContainer params={params} />
              {params?.projectID && !hideKioskConnectivity ? (
                <PermissionBoundary
                  permissions={[
                    {
                      action: Permissions.hubs.list,
                      projectID: params.projectID
                    }
                  ]}
                >
                  <KioskConnectivityStatusBarContainer />
                </PermissionBoundary>
              ) : null}
            </AppBar>
            <div className={classes.drawerRoot}>
              <Container className={classes.container}>{children || ''}</Container>
              <ProjectSummaryDrawerContainer params={params} />
              <SearchDrawerContainer params={params} />
            </div>
          </SearchContextProvider>
        </ProjectSummaryContextProvider>
      )}
    </Box>
  );
};
